/**
 * Before the visit (Isaiah Cotto, 2026-11-03; T0, dark): the buildings planned in the next 14 days, and who needs a
 * look before the provider goes. April Torino's pre-visit benefit check is Manifest Review's main function; the
 * after-visit review stays beside it as the VAs' reconciliation tool.
 *
 * Read-only: the table opens a building's residents docked beside it and offers no act. Shown only when the server
 * says previsit_enabled (MANIFEST_PREVISIT), so a dark build has no tab and no request.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { refusalWords } from './resubmittalsApi';
import MfBoard, { useOpenParam, useClientSort, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { previsitApi, type PrevisitBuilding, type PrevisitList } from './previsitApi';
import PrevisitPanel, { WHY_ORDER, WHY_LABEL, WHY_MEANS, WhyChip, mdy } from './PrevisitPanel';

const SORTERS: Record<string, (r: PrevisitBuilding) => string | number | null> = {
  visit: (r) => r.next_visit,
  listed: (r) => r.listed,
};

export default function PrevisitDesk() {
  const [data, setData] = useState<PrevisitList | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await previsitApi.list();
      if (r.success && r.data) setData(r.data);
      else setProblem(refusalWords(r, 'Could not load the buildings to be visited.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.items || [];
  const sort = useClientSort<PrevisitBuilding>(rows, SORTERS);
  const listed = rows.reduce((n, r) => n + r.listed, 0);
  const legend: MfLegendItem[] = WHY_ORDER.map((w) => ({
    key: w, label: WHY_LABEL[w], sample: <WhyChip why={w} />, meaning: WHY_MEANS[w], count: rows.reduce((n, r) => n + r.counts[w], 0),
  }));
  const columns: BoardColumn<PrevisitBuilding>[] = [
    { key: 'visit', label: 'Next visit', sort: 'visit', help: 'The earliest planned or confirmed visit in the next 14 days.', render: (r) => <span>{mdy(r.next_visit)} · {r.state === 'CONFIRMED' ? 'Confirmed' : 'Planned'}</span> },
    { key: 'provider', label: 'Provider', help: 'The provider the visit plan names.', render: (r) => (r.provider_name ? <span>{r.provider_name}</span> : <span className="board-na">not named</span>) },
    ...WHY_ORDER.map((w) => ({ key: w, label: WHY_LABEL[w], numeric: true, help: WHY_MEANS[w], render: (r: PrevisitBuilding) => <span>{r.counts[w]}</span> })),
    { key: 'listed', label: 'To look at', sort: 'listed', numeric: true, help: 'Residents listed for any reason.', render: (r) => <span>{r.listed}</span> },
    { key: 'excluded', label: 'Not listed', help: 'With an audiogram in the last 365 days, enrolled, or no reason to look.', render: (r) => <span>{r.excluded.recent_audiogram} recent audiogram · {r.excluded.enrolled} enrolled · {r.excluded.other} other</span> },
  ];
  const identity = {
    label: 'Building', help: 'A building with a visit planned in the next 14 days. Open it for the residents, docked beside the table.',
    render: (r: PrevisitBuilding) => <span className="board-clip" title={r.facility_name || ''}>{r.facility_name || 'Building'}</span>,
    name: (r: PrevisitBuilding) => r.facility_name || 'Building',
  };
  const sections = (r: PrevisitBuilding): BoardDetailSection[] => [{ id: 'residents', label: 'Residents to look at', render: () => <PrevisitPanel key={r.facility_id} facilityId={r.facility_id} /> }];

  let state: React.ReactNode = null;
  if (problem) state = <MfLoadFailure message={problem} retry={load} />;
  else if (!data) state = <MfSkeleton bare />;
  else if (rows.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing planned" message="No building has a visit planned or confirmed in the next 14 days." />;

  return (
    <main className="mf-main mf-main--board" data-testid="previsit-desk">
      <MfBoard<PrevisitBuilding>
        title="Before the visit"
        count={data ? rows.length : null}
        units={['building', 'buildings']}
        what="Buildings with a visit planned in the next 14 days, and the residents to look at first: new referrals, annual reviews due, and Medicaid issues with no audiogram in the last year. Nothing here changes a record."
        legend={rows.length ? <MfLegend title="Why a resident is listed" items={legend} whole={listed} units={['resident', 'residents']} wholeWords="listed in these buildings" /> : undefined}
        state={state}
        columns={columns}
        identity={identity}
        rows={sort.rows}
        rowKey={(r) => r.facility_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${r.facility_name || 'Building'}: residents to look at`}
        sort={sort.sort}
        dir={sort.dir}
        sorts={sort.sorts}
        onSort={sort.onSort}
        notes={['A resident with two reasons is counted under each.']}
      />
    </main>
  );
}
